import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, KineticLines, Kicker } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { ZeoliteCage } from "@/components/ZeoliteCage";

const STATS = ["SOLE-SOURCE ACTIVE", "PATENT-PENDING", "MADISON, WISCONSIN, USA", "B2B SUPPLY ONLY"];

const VALUES = [
  {
    n: "01",
    title: "Proprietary active",
    body: "A mineral adsorbent engineered for selective affinity to methylxanthine alkaloids, supplied for use in products intended to support veterinary management of chocolate ingestion in dogs.",
  },
  {
    n: "02",
    title: "Sole-source supply",
    body: "The active is produced exclusively by Chocolate Detox in Wisconsin under a trade-secret process. One supplier, one specification, one accountable partner.",
  },
  {
    n: "03",
    title: "Licensing by territory",
    body: "Exclusive territory licences available to qualified manufacturers, licensees and distributors, with technical and regulatory support for partner registrations.",
  },
];

const CHAPTERS = [
  {
    n: "01",
    title: "The Molecule",
    body: "A proprietary mineral adsorbent engineered with selective affinity for methylxanthines (theobromine and caffeine), the compounds that make chocolate hazardous to dogs. Composition is disclosed only under executed NDA.",
  },
  {
    n: "02",
    title: "The Gap",
    body: "Chocolate ingestion is among the most common causes of canine poison-control calls, with reported incidence spiking around holidays. Current management relies on emesis followed by activated charcoal, an approach with recognized practical limitations in veterinary practice.",
  },
  {
    n: "03",
    title: "The Supply",
    body: "A proprietary process, held as a trade secret, makes Chocolate Detox the sole source of the active. Partners receive security of supply, batch-level documentation and formulation support from a single accountable source.",
  },
  {
    n: "04",
    title: "The Governance",
    body: "Patent coverage over the formulated product and method of use, layered over a trade-secret process that is never licensed, only supplied. Territories are licensed selectively to qualified partners.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero-grid pt-32 lg:pt-40 pb-16 lg:pb-24 border-b border-line" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="font-mono text-xs uppercase tracking-[0.22em] text-pine font-medium flex items-center gap-3"
            >
              <span className="inline-block h-px w-8 bg-pine/50" aria-hidden="true" />
              Specialty ingredient supply · Madison, Wisconsin
            </motion.p>
            <h1 className="mt-7 font-headline font-semibold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.06]">
              <KineticLines
                lines={[
                  <>The <span className="text-pine">sole source</span> of a</>,
                  "proprietary adsorbent active",
                  "for canine methylxanthine",
                  "toxicosis.",
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 text-base sm:text-lg text-smoke leading-relaxed max-w-2xl"
            >
              Chocolate ingestion is among the most common causes of canine poison-control calls,
              and reported incidence spikes around holidays. We develop and supply the proprietary
              mineral adsorbent active engineered for veterinary products intended to address it.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/partnering"
                data-testid="hero-partnership-cta"
                className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover text-white text-sm font-medium px-6 py-3.5 transition-colors duration-200"
              >
                Partnership enquiries <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/technology"
                data-testid="hero-technology-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-pine border border-line hover:border-pine/50 px-6 py-3.5 transition-colors duration-200"
              >
                The technology <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <ZeoliteCage />
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 border border-line bg-white divide-x divide-line divide-y lg:divide-y-0" data-testid="hero-stats">
            {STATS.map((s, i) => (
              <Reveal key={s} delay={i * 0.08} className="px-5 py-4">
                <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-smoke">{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE BLOCKS */}
      <section className="py-20 lg:py-28" data-testid="home-value-blocks">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker index="§1">Why partners evaluate us</Kicker>
            <h2 className="mt-5 font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
              A single active, a single source, and a clear path to market.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-line border border-line">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.1} className="bg-white p-8 lg:p-10 group hover:bg-pinelight/40 transition-colors duration-300">
                <p className="font-mono text-xs text-pine">{v.n}</p>
                <h3 className="mt-5 font-headline font-semibold text-xl text-ink tracking-tight">{v.title}</h3>
                <p className="mt-4 text-sm text-smoke leading-relaxed">{v.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="py-20 lg:py-28 bg-white border-y border-line" data-testid="home-manifesto">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker index="§2">Position, in four chapters</Kicker>
          </Reveal>
          <div className="mt-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-9 border-t border-line first:border-t-0 group">
                  <p className="md:col-span-2 font-mono text-sm text-pine pt-1">{c.n}</p>
                  <h3 className="md:col-span-4 font-headline font-semibold text-2xl lg:text-3xl tracking-tight text-ink group-hover:text-pine transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="md:col-span-6 text-sm sm:text-base text-smoke leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* CTA */}
      <section className="bg-obsidian text-white py-20 lg:py-28" data-testid="home-cta-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-pinelight/70">Licensing status · Territories open</p>
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
                Evaluate the active for your veterinary portfolio.
              </h2>
              <p className="mt-5 text-white/60 text-base leading-relaxed max-w-xl">
                Safety and efficacy have been demonstrated in gold-standard trials conducted by
                independent third-party CROs and university research programs. Binding data and
                analytical characterization are available to qualified partners under executed NDA.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.15}>
              <Link
                to="/partnering"
                data-testid="home-cta-partnering-button"
                className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover text-white text-sm font-medium px-7 py-4 transition-colors duration-200"
              >
                Partnership enquiries <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
